import { useState, useRef, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';

export interface CommitFile {
  filename: string;
  additions: number;
  deletions: number;
  changes: number;
}

interface CommitFilesData {
  files: CommitFile[];
  loading: boolean;
  error: Error | null;
}

interface UseWorkspaceCommitDetailsResult {
  commitFiles: Record<string, CommitFilesData>;
  fetchDetails: (commitId: string) => Promise<void>;
  getCommitFiles: (commitId: string) => CommitFile[];
  isLoading: (commitId: string) => boolean;
  getError: (commitId: string) => Error | null;
}

/**
 * Hook for fetching file details for specific commits
 * Handles multiple commits independently to avoid race conditions
 * @param workspaceId The workspace ID
 */
export const useWorkspaceCommitDetails = (
  workspaceId: string | undefined
): UseWorkspaceCommitDetailsResult => {
  const authenticatedApi = useAuthenticatedApi();
  const [commitFiles, setCommitFiles] = useState<Record<string, CommitFilesData>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  const fetchDetails = useCallback(async (commitId: string) => {
    if (!workspaceId || !commitId) return;

    // Cancel any existing request for this commit
    if (abortControllersRef.current[commitId]) {
      abortControllersRef.current[commitId].abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllersRef.current[commitId] = abortController;

    // Set loading state for this specific commit
    setCommitFiles(prev => ({
      ...prev,
      [commitId]: {
        files: prev[commitId]?.files || [],
        loading: true,
        error: null,
      }
    }));

    try {
      const data = await authenticatedApi.get<{ files: CommitFile[] }>(
        `/workspaces/${workspaceId}/commits/${commitId}/details`,
        { signal: abortController.signal }
      );

      // Only update if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setCommitFiles(prev => ({
          ...prev,
          [commitId]: {
            files: data.files || [],
            loading: false,
            error: null,
          }
        }));
      }
    } catch (err: any) {
      // Don't set error state if request was aborted
      if (err.name !== 'AbortError' && !abortController.signal.aborted) {
        console.error(`Error fetching commit details for ${commitId}:`, err);
        setCommitFiles(prev => ({
          ...prev,
          [commitId]: {
            files: prev[commitId]?.files || [],
            loading: false,
            error: err as Error,
          }
        }));
      }
    } finally {
      // Clean up abort controller
      delete abortControllersRef.current[commitId];
    }
  }, [workspaceId, authenticatedApi]);

  const getCommitFiles = useCallback((commitId: string): CommitFile[] => {
    return commitFiles[commitId]?.files || [];
  }, [commitFiles]);

  const isLoading = useCallback((commitId: string): boolean => {
    return commitFiles[commitId]?.loading || false;
  }, [commitFiles]);

  const getError = useCallback((commitId: string): Error | null => {
    return commitFiles[commitId]?.error || null;
  }, [commitFiles]);

  return { commitFiles, fetchDetails, getCommitFiles, isLoading, getError };
};
